/* Roadmap — public feature-request kanban with upvote support.
 *
 * 4 columns:
 *   • Suggested    (status=open)         — fresh ideas waiting for triage
 *   • Planned      (status=planned)
 *   • In progress  (status=in_progress)
 *   • Shipped      (status=shipped)
 *
 * Every authenticated user (buyer/seller/admin) can read + upvote.
 * Admins can drag tickets between columns from /admin/support.
 *
 * Uses GET /api/roadmap + POST/DELETE /api/roadmap/{id}/upvote.
 */
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/PortalShell";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Lightbulb, ArrowUp, CheckCircle2, Map, Sparkles, ChevronRight,
  Plus, Loader2, Search,
} from "lucide-react";

const COLUMNS = [
  {
    key: "open",
    label: "Suggested",
    desc: "Fresh ideas waiting for triage",
    chip: "bg-foreground/5 text-foreground/65 border-border",
    dot: "bg-foreground/40",
  },
  {
    key: "planned",
    label: "Planned",
    desc: "Coming soon — vote to prioritise",
    chip: "bg-blue-50 text-blue-700 border-blue-200",
    dot: "bg-blue-500",
  },
  {
    key: "in_progress",
    label: "In progress",
    desc: "Actively being built",
    chip: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
  },
  {
    key: "shipped",
    label: "Shipped",
    desc: "Live in verqo",
    chip: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
  },
];

export default function Roadmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [submittingVote, setSubmittingVote] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/roadmap");
      setData(r.data);
    } catch {
      setData({ open: [], planned: [], in_progress: [], shipped: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleVote = async (item) => {
    setSubmittingVote(item.id);
    const wasVoted = item.voted_by_me;
    // Optimistic
    setData((d) => {
      if (!d) return d;
      const next = { ...d };
      for (const k of Object.keys(next)) {
        next[k] = next[k].map((r) =>
          r.id === item.id
            ? {
                ...r,
                voted_by_me: !wasVoted,
                votes_count: (r.votes_count || 0) + (wasVoted ? -1 : 1),
              }
            : r,
        );
      }
      return next;
    });
    try {
      if (wasVoted) {
        await api.delete(`/roadmap/${item.id}/upvote`);
      } else {
        await api.post(`/roadmap/${item.id}/upvote`);
      }
    } catch {
      // Revert on error
      toast.error("Couldn't update vote");
      load();
    } finally {
      setSubmittingVote(null);
    }
  };

  const requestNew = () => {
    window.dispatchEvent(new CustomEvent("open-help-ticket", {
      detail: { type: "feature" },
    }));
  };

  const filtered = useMemo(() => {
    if (!data) return null;
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    const filt = (arr) => arr.filter(r =>
      (r.subject || "").toLowerCase().includes(needle) ||
      (r.body || "").toLowerCase().includes(needle),
    );
    return Object.fromEntries(Object.entries(data).map(([k, v]) => [k, filt(v)]));
  }, [data, search]);

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return Object.values(data).reduce((s, arr) => s + (arr?.length || 0), 0);
  }, [data]);

  return (
    <div data-testid="roadmap-page">
      <PageHeader
        title="Roadmap"
        subtitle="Vote on what we build next · ideas come straight from you"
        action={
          <Button
            onClick={requestNew}
            data-testid="roadmap-suggest-btn"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90"
          >
            <Plus className="w-4 h-4 mr-1.5"/>Suggest a feature
          </Button>
        }
      />

      <div className="p-8 space-y-6">
        {/* Summary banner */}
        <div className="rounded-2xl border border-border bg-white px-5 py-4 flex items-center gap-4" data-testid="roadmap-banner">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-foreground text-background">
            <Map className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Help shape verqo</div>
            <div className="text-[11.5px] text-foreground/60 mt-0.5">
              {totalCount === 0
                ? "No suggestions yet — be the first."
                : `${totalCount} suggestion${totalCount === 1 ? "" : "s"} from the community. Click ▲ to upvote — most-voted ideas get prioritised.`}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="w-3.5 h-3.5 text-foreground/40 absolute top-1/2 -translate-y-1/2 left-3"/>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search roadmap…"
            data-testid="roadmap-search"
            className="w-full h-10 rounded-lg border border-border bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/10"
          />
        </div>

        {/* 4-column kanban */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-foreground/55 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2"/>Loading the roadmap…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="roadmap-columns">
            {COLUMNS.map((col) => {
              const items = (filtered?.[col.key]) || [];
              return (
                <div key={col.key} className="rounded-2xl border border-border bg-secondary/30 overflow-hidden" data-testid={`roadmap-col-${col.key}`}>
                  <div className="px-4 py-3 border-b border-border bg-white">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${col.dot}`}/>
                      <span className="text-[11px] mono uppercase tracking-[0.16em] text-foreground/65">{col.label}</span>
                      <span className="ml-auto text-[10.5px] mono text-foreground/45">{items.length}</span>
                    </div>
                    <div className="text-[10.5px] text-foreground/55 mt-1">{col.desc}</div>
                  </div>
                  <div className="p-2.5 space-y-2 min-h-[120px]">
                    {items.length === 0 ? (
                      <div className="text-center py-6 text-[11.5px] text-foreground/45">
                        {col.key === "open" ? "Be the first to suggest something" : "Nothing here yet"}
                      </div>
                    ) : (
                      items.map((item) => (
                        <FeatureCard
                          key={item.id}
                          item={item}
                          onToggleVote={toggleVote}
                          submitting={submittingVote === item.id}
                          shippedColumn={col.key === "shipped"}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state CTA — render only when zero items across the board */}
        {!loading && totalCount === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-border p-10 text-center" data-testid="roadmap-empty">
            <Lightbulb className="w-8 h-8 text-foreground/40 mx-auto mb-3"/>
            <div className="text-base font-medium">No suggestions yet</div>
            <div className="text-[12.5px] text-foreground/60 mt-1 max-w-md mx-auto">
              The roadmap is community-driven. Click "Suggest a feature" to add the first idea — every verqo user can upvote it.
            </div>
            <Button onClick={requestNew} className="mt-4 rounded-full bg-foreground text-background hover:bg-foreground/90">
              <Plus className="w-4 h-4 mr-1.5"/>Suggest the first feature
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function FeatureCard({ item, onToggleVote, submitting, shippedColumn }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="rounded-xl border border-border bg-white p-3 hover:border-foreground/20 transition-colors"
      data-testid={`feature-card-${item.id}`}
    >
      <div className="flex items-start gap-2.5">
        {/* Vote button (or shipped checkmark) */}
        {shippedColumn ? (
          <div className="w-9 shrink-0 grid place-items-center pt-1" title="Shipped">
            <CheckCircle2 className="w-5 h-5 text-emerald-600"/>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onToggleVote(item)}
            disabled={submitting}
            data-testid={`vote-${item.id}`}
            className={`w-9 shrink-0 rounded-md border flex flex-col items-center justify-center py-1.5 transition-colors ${item.voted_by_me ? "bg-foreground border-foreground text-background" : "bg-white border-border text-foreground/65 hover:border-foreground/40"} ${submitting ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
            aria-label={item.voted_by_me ? "Remove vote" : "Upvote"}
          >
            <ArrowUp className="w-3 h-3"/>
            <span className="text-[10.5px] font-semibold mono mt-0.5">
              {item.votes_count || 0}
            </span>
          </button>
        )}
        {/* Content */}
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="block text-left w-full"
            data-testid={`feature-toggle-${item.id}`}
          >
            <div className="text-[12.5px] font-medium leading-snug">
              {item.subject}
            </div>
          </button>
          {expanded && (
            <div className="mt-1.5 text-[11.5px] text-foreground/65 whitespace-pre-wrap leading-relaxed">
              {item.body}
            </div>
          )}
          <div className="mt-1 flex items-center gap-2 text-[10px] mono uppercase tracking-[0.14em] text-foreground/45">
            <span>#{item.number}</span>
            {item.created_at && (
              <span>· {new Date(item.created_at).toLocaleDateString()}</span>
            )}
            {!expanded && item.body && item.body.length > 0 && (
              <span className="ml-auto inline-flex items-center gap-0.5 hover:text-foreground/65 cursor-pointer" onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>
                Read more <ChevronRight className="w-2.5 h-2.5"/>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
